import { packingListContainerKey } from '../packingListContainer';
import { isRwNrPackingListRow } from '../packingListRwNr';
import { sortPackingListDetailByGradeWithinContainer } from '../packingListGradeSort';
import {
  formatPackingListM3,
  formatPackingListWidthLength,
  parsePlNumberInput,
} from '../packingListPlNumberFormat';

function uniqueContainersFromLineas(lineas) {
  const seen = new Set();
  const out = [];
  for (const row of lineas || []) {
    const k = packingListContainerKey(row).trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

function safe(s) {
  return s == null ? '' : String(s);
}

function parseNum(v) {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** m³ en Grade Height: máximo 3 decimales (evita 31.345999… al sumar). */
export function roundGradeHeightM3(n) {
  if (n == null || !Number.isFinite(n)) return null;
  return Math.round(n * 1000) / 1000;
}

/** Decimales visibles en el valor original del packing list (texto o número). */
function fractionDigitsInSource(val) {
  if (val == null || val === '') return 0;
  if (typeof val === 'number' && Number.isFinite(val)) {
    const s = String(val);
    const dot = s.indexOf('.');
    return dot >= 0 ? s.length - dot - 1 : 0;
  }
  const s = String(val).trim().replace(/\s/g, '');
  const comma = s.lastIndexOf(',');
  const dot = s.lastIndexOf('.');
  const sep = comma > dot ? comma : dot;
  if (sep < 0) return 0;
  const frac = s.slice(sep + 1).replace(/[^\d]/g, '');
  return frac.length;
}

/** Thickness/Height y espesor CD: mismo criterio que la grilla del PL (coma decimal, decimales completos). */
function formatGradeHeightMeasure(val) {
  if (val == null || val === '') return '';
  const n = parseNum(val);
  if (n == null) return safe(val).trim();
  const fromSource = fractionDigitsInSource(val);
  const fromNumber = Number.isInteger(n) ? 0 : fractionDigitsInSource(n);
  const frac = Math.max(fromSource, fromNumber);
  const minFrac = frac > 0 ? frac : 0;
  return n.toLocaleString('es-CL', {
    minimumFractionDigits: minFrac,
    maximumFractionDigits: Math.max(minFrac, 3),
  });
}

function normalizedGrado(row) {
  return safe(row?.grado_celda).trim().toUpperCase().replace(/\s+/g, ' ');
}

function isNrRwGradoToken(g) {
  if (!g) return false;
  if (g === 'RW' || g === 'NR' || g === 'WR') return true;
  return /\bNR\s*\/\s*RW\b|\bRW\s*\/\s*NR\b/.test(g);
}

/** Etiqueta Grade (CD / NR / RW según packing list). */
export function gradeHeightGradeLabel(row) {
  const g = normalizedGrado(row);
  if (g === 'CD' || g === 'NR' || g === 'RW' || g === 'WR') return g === 'WR' ? 'RW' : g;
  if (isNrRwGradoToken(g)) return g.replace(/\s+/g, '');
  if (isRwNrPackingListRow(row)) {
    if (/\bRW\b/.test(g) || /^\s*RW\s*$/i.test(safe(row?.ancho))) return 'RW';
    return 'NR';
  }
  return g || 'CD';
}

function piezasPorPaqueteRaw(row) {
  const v = row?.piezas_por_paquete ?? row?.pcs_por_paq;
  return v == null ? '' : v;
}

/** CD → espesor; RW / NR / NR·RW (y filas RW·NR del PL) → piezas por paquete del Excel cargado. */
function thicknessUsesPiezasPorPaquete(row) {
  const g = normalizedGrado(row);
  if (isNrRwGradoToken(g)) return true;
  if (isRwNrPackingListRow(row)) return true;
  const label = gradeHeightGradeLabel(row);
  return label === 'RW' || label === 'NR';
}

/**
 * Thickness/Height: CD → espesor; NR/RW/NR·RW → piezas por paquete (pcs/pqt).
 */
export function gradeHeightThickness(row) {
  if (!thicknessUsesPiezasPorPaquete(row)) {
    return formatGradeHeightMeasure(row?.espesor);
  }
  return formatGradeHeightMeasure(piezasPorPaqueteRaw(row));
}

export function gradeHeightWidth(row) {
  const a = row?.ancho;
  if (a == null || a === '') return '';
  const s = safe(a).trim();
  if (/^RW$/i.test(s)) return 'RW';
  const formatted = formatPackingListWidthLength(a);
  if (formatted) return formatted;
  return s;
}

export function gradeHeightLength(row) {
  if (row?.largo == null || row?.largo === '') return '';
  return formatPackingListWidthLength(row.largo);
}

function gradeHeightWidthValue(row) {
  const a = row?.ancho;
  if (a == null || a === '') return null;
  if (/^RW$/i.test(safe(a).trim())) return null;
  return parsePlNumberInput(a);
}

function gradeHeightLengthValue(row) {
  if (row?.largo == null || row?.largo === '') return null;
  return parsePlNumberInput(row.largo);
}

export function gradeHeightPiecesPerBundle(row) {
  const pcs = piezasPorPaqueteRaw(row);
  const n = parseNum(pcs);
  return n != null ? n : safe(pcs).trim() || null;
}

export function gradeHeightBundles(row) {
  return parseNum(row?.cantidad_paquetes);
}

function gradeHeightTotalM3Value(row) {
  return roundGradeHeightM3(parseNum(row?.total_m3));
}

export function gradeHeightTotalM3(row) {
  const n = gradeHeightTotalM3Value(row);
  if (n == null) return '';
  return formatPackingListM3(n);
}

export function lineToGradeHeightDetailRow(row, showContainer) {
  return {
    container: showContainer ? packingListContainerKey(row) : '',
    grade: gradeHeightGradeLabel(row),
    piecesPerBundle: gradeHeightPiecesPerBundle(row),
    bundles: gradeHeightBundles(row),
    thicknessHeight: gradeHeightThickness(row),
    width: gradeHeightWidth(row),
    widthValue: gradeHeightWidthValue(row),
    length: gradeHeightLength(row),
    lengthValue: gradeHeightLengthValue(row),
    total: gradeHeightTotalM3(row),
    totalValue: gradeHeightTotalM3Value(row),
  };
}

/**
 * @returns {{ container: string, detailRows: object[], totalM3: string, totalM3Value: number|null }[]}
 */
export function gradeHeightGroupsFromLineas(lineas) {
  const containers = uniqueContainersFromLineas(lineas);
  const sorted = sortPackingListDetailByGradeWithinContainer(lineas || []);
  return containers
    .map((container) => {
      const lines = sorted.filter((r) => packingListContainerKey(r) === container);
      const detailRows = lines.map((r, i) => lineToGradeHeightDetailRow(r, i === 0));
      const totalM3Num = roundGradeHeightM3(
        lines.reduce((s, r) => s + (gradeHeightTotalM3Value(r) ?? 0), 0)
      );
      const totalM3 = totalM3Num == null ? '' : formatPackingListM3(totalM3Num);
      return { container, detailRows, totalM3, totalM3Value: totalM3Num };
    })
    .filter((g) => g.detailRows.length > 0);
}
